/** Module configuration types for customer-overridable labels. */

export interface ModuleConfig {
  display_name: string;
  features: Record<string, Record<string, string>>;
}

// ---------------------------------------------------------------------------
// Per-module typed interfaces (feature → label keys)
// ---------------------------------------------------------------------------

export interface IemFrontWindowLabels {
  menu_label: string;
  conflicting_roles_message: string;
  issue_workflow_title: string;
  turn_in_workflow_title: string;
  exchange_workflow_title: string;
  main_content_tabs_label: string;
}

export interface IemIdentityLabels {
  person_label: string;
  person_label_plural: string;
  individual_id_label: string;
  individual_id_prefix: string;
  front_window_person_label: string;
  front_window_person_label_plural: string;
  reports_person_label: string;
  reports_person_label_plural: string;
  global_search_person_label: string;
  global_search_person_label_plural: string;
}

export interface IemIeKitsLabels {
  menu_label: string;
  ie_kit_label: string;
  ie_kit_label_plural: string;
  ie_kit_id_prefix: string;
  front_window_tab_label: string;
  front_window_column_label: string;
  reports_label: string;
  reports_label_plural: string;
  global_search_label: string;
  global_search_label_plural: string;
}

export interface IemLocationCategoryLabels {
  primary_label: string;
  secondary_label: string;
  related_label: string;
}

export interface IemModuleConfig {
  display_name: string;
  features: { front_window: IemFrontWindowLabels; identity: IemIdentityLabels; ie_kits: IemIeKitsLabels; location_categories: IemLocationCategoryLabels };
}

export interface CatalogLocationLabels {
  primary_location_label: string;
  secondary_location_label: string;
}

export interface CatalogDashboardLabels {
  reorder_point_column_label: string;
}

export interface CatalogModuleConfig {
  display_name: string;
  features: { catalog: { available_stock_label: string; assigned_stock_label: string }; location_labels: CatalogLocationLabels; dashboard_labels: CatalogDashboardLabels };
}

export interface SupplyModuleConfig {
  display_name: string;
  features: {
    purchase_requisitions: {
      asset_authority_label: string;
      asset_authority_default: string;
      asset_authority_helper: string;
    };
  };
}

export interface DistributionModuleConfig {
  display_name: string;
  features: {
    transfers_shipping: {
      issue_from_default: string;
      dd1149_from_address: string;
      dd1149_shipping_address: string;
    };
  };
}

export interface DocumentsModuleConfig {
  display_name: string;
  features: {
    dd200: { block9_label: string };
    contamination_memo: { iem_field_label: string };
  };
}

// ---------------------------------------------------------------------------
// All known module keys (order controls sidebar display)
// ---------------------------------------------------------------------------

export const MODULE_KEYS = [
  'catalog',
  'distribution',
  'documents',
  'iem',
  'locations',
  'reports',
  'supply',
  'support',
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

// ---------------------------------------------------------------------------
// Defaults — used as fallbacks when config has not been loaded or is missing.
// These are the product-standard "IEM" defaults. Customer-specific overrides
// come from the database via a deployment-specific seed file.
// ---------------------------------------------------------------------------

export const MODULE_DEFAULTS: Record<string, ModuleConfig> = {
  iem: {
    display_name: 'Equipment',
    features: {
      front_window: {
        menu_label: 'Front Window',
        conflicting_roles_message:
          'Front Window Access Only - Your account has conflicting roles. You have been limited to Front Window access.',
        issue_workflow_title: 'IEM Issue Workflow',
        turn_in_workflow_title: 'IEM Turn-In Workflow',
        exchange_workflow_title: 'IEM Exchange Workflow',
        main_content_tabs_label: 'IEM main content tabs',
      },
      identity: {
        person_label: 'Individual',
        person_label_plural: 'Individuals',
        individual_id_label: 'Individual ID',
        individual_id_prefix: 'INDV',
        front_window_person_label: 'Individual',
        front_window_person_label_plural: 'Individuals',
        reports_person_label: 'Individual',
        reports_person_label_plural: 'Individuals',
        global_search_person_label: 'Individual',
        global_search_person_label_plural: 'Individuals',
      },
      ie_kits: {
        menu_label: 'IE Kit Management',
        ie_kit_label: 'IE Kit',
        ie_kit_label_plural: 'IE Kits',
        ie_kit_id_prefix: 'IE',
        front_window_tab_label: 'IE Kit',
        front_window_column_label: 'IE Kit',
        reports_label: 'IE Kit',
        reports_label_plural: 'IE Kits',
        global_search_label: 'IE Kit',
        global_search_label_plural: 'IE Kits',
      },
      location_categories: {
        primary_label: 'Primary',
        secondary_label: 'Secondary',
        related_label: 'Related',
      },
    },
  },
  catalog: {
    display_name: 'Catalog',
    features: {
      catalog: {
        available_stock_label: 'Update IEM Available Stock Assets only',
        assigned_stock_label: 'Update individual HR records only',
      },
      location_labels: {
        primary_location_label: 'Primary Location',
        secondary_location_label: 'Secondary Location',
      },
      dashboard_labels: {
        reorder_point_column_label: 'Threshold',
      },
    },
  },
  supply: {
    display_name: 'Supply',
    features: {
      purchase_requisitions: {
        asset_authority_label: 'Asset Authority',
        asset_authority_default: 'IEM',
        asset_authority_helper: 'Defaults to IEM',
      },
    },
  },
  distribution: {
    display_name: 'Distribution',
    features: {
      transfers_shipping: {
        issue_from_default: 'IEM',
        dd1149_from_address: 'Supply Office\nFort Liberty, NC 28310',
        dd1149_shipping_address: 'Property Book Office\nFort Liberty, NC 28310',
      },
    },
  },
  documents: {
    display_name: 'Documents',
    features: {
      dd200: { block9_label: 'IEM' },
      contamination_memo: { iem_field_label: 'IEM' },
    },
  },
  locations: {
    display_name: 'Locations',
    features: {},
  },
  reports: {
    display_name: 'Reports',
    features: {
      reports: {
        report_footer_text:
          'Confidential \u2013 For authorized personnel use only.',
      },
    },
  },
  support: {
    display_name: 'Support',
    features: {},
  },
};
